import { ZOOM_SCROLL_CONTAINER_SELECTOR } from '@/constants/floor-guide';
import { useLayoutEffect } from 'react';

const PAN_THRESHOLD_PX = 10;
const INTERACTIVE_SELECTOR = '[data-interactive], button, a, input, textarea, select, [role="button"]';
const SCROLLABLE_SELECTOR = '[class*="scrollbar"], .overflow-y-auto, .custom-scrollbar, .overflow-auto';

const applyMagnifyScroll = (deltaX: number, deltaY: number, zoomContainer: HTMLElement | null) => {
    if (zoomContainer) {
        const canScrollY = zoomContainer.scrollHeight > zoomContainer.clientHeight;
        const canScrollX = zoomContainer.scrollWidth > zoomContainer.clientWidth;

        if (canScrollY || canScrollX) {
            zoomContainer.scrollTop += deltaY;
            zoomContainer.scrollLeft += deltaX;
            return;
        }
    }

    window.scrollBy({
        left: deltaX,
        top: deltaY,
        behavior: 'instant',
    });
};

const isScrollableElement = (element: HTMLElement) => {
    const style = window.getComputedStyle(element);
    const canScrollY =
        (style.overflowY === 'auto' || style.overflowY === 'scroll') && element.scrollHeight > element.clientHeight;
    const canScrollX =
        (style.overflowX === 'auto' || style.overflowX === 'scroll') && element.scrollWidth > element.clientWidth;

    return canScrollY || canScrollX;
};

const shouldIgnoreMagnifyScroll = (target: EventTarget | null) => {
    if (!(target instanceof Element)) {
        return false;
    }

    if (target.closest(INTERACTIVE_SELECTOR)) {
        return true;
    }

    const scrollableAncestor = target.closest(SCROLLABLE_SELECTOR);
    if (scrollableAncestor instanceof HTMLElement && isScrollableElement(scrollableAncestor)) {
        return true;
    }

    return false;
};

export const useMagnifyMapScrollThrough = (enabled: boolean, isReady = true) => {
    useLayoutEffect(() => {
        if (!enabled || !isReady) {
            return;
        }

        const zoomContainer = document.querySelector(ZOOM_SCROLL_CONTAINER_SELECTOR) as HTMLElement | null;

        let pointerStartX = 0;
        let pointerStartY = 0;
        let isPanning = false;
        let activePointerId: number | null = null;
        let isPointerGestureIgnored = false;

        const handleWheel = (event: WheelEvent) => {
            if (shouldIgnoreMagnifyScroll(event.target)) {
                return;
            }

            applyMagnifyScroll(event.deltaX, event.deltaY, zoomContainer);
            event.preventDefault();
            event.stopImmediatePropagation();
        };

        const handlePointerDown = (event: PointerEvent) => {
            if (event.pointerType === 'mouse' && event.button !== 0) {
                return;
            }

            isPointerGestureIgnored = shouldIgnoreMagnifyScroll(event.target);
            if (isPointerGestureIgnored) {
                return;
            }

            activePointerId = event.pointerId;
            pointerStartX = event.clientX;
            pointerStartY = event.clientY;
            isPanning = false;
        };

        const handlePointerMove = (event: PointerEvent) => {
            if (isPointerGestureIgnored || event.pointerId !== activePointerId) {
                return;
            }

            if (!isPanning) {
                const distance = Math.hypot(event.clientX - pointerStartX, event.clientY - pointerStartY);
                if (distance < PAN_THRESHOLD_PX) {
                    return;
                }
                isPanning = true;
            }

            const deltaX = pointerStartX - event.clientX;
            const deltaY = pointerStartY - event.clientY;

            applyMagnifyScroll(deltaX, deltaY, zoomContainer);
            pointerStartX = event.clientX;
            pointerStartY = event.clientY;
            event.preventDefault();
            event.stopImmediatePropagation();
        };

        const resetPointerState = (event: PointerEvent) => {
            if (event.pointerId !== activePointerId) {
                return;
            }

            if (isPanning) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }

            activePointerId = null;
            isPanning = false;
            isPointerGestureIgnored = false;
        };

        document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
        document.addEventListener('pointerdown', handlePointerDown, { capture: true });
        document.addEventListener('pointermove', handlePointerMove, { capture: true, passive: false });
        document.addEventListener('pointerup', resetPointerState, { capture: true, passive: false });
        document.addEventListener('pointercancel', resetPointerState, { capture: true });

        return () => {
            document.removeEventListener('wheel', handleWheel, { capture: true });
            document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
            document.removeEventListener('pointermove', handlePointerMove, { capture: true });
            document.removeEventListener('pointerup', resetPointerState, { capture: true });
            document.removeEventListener('pointercancel', resetPointerState, { capture: true });
        };
    }, [enabled, isReady]);
};
